import { useMemo } from "react";
import { resources } from "@constants/hardcoded-strings";
import { AxnCountry } from "@idw-types/data/reference-data/axn";
import { WorkflowReportVolumesByCountryResponse } from "@idw-types/data/reports";
import { CardContainer } from "../../../common/card-container/card-container";
import { ContentPageBody } from "../../../common/content-page-body/content-page-body";
import { ContentPageHeader } from "../../../common/content-page-header/content-page-header";
import { WorkflowReportSearchFiltersState } from "@idw-types/state/workflow-report-state";
import styles from "./workflow-report-volumes-by-country-container.less";
import { WorkflowReportVolumesByCountryChart } from "./workflow-report-volumes-by-country-container-chart/workflow-report-volumes-country-chart";
import { LoadingIndicator } from "@containers/common/loading-indicator/loading-indicator";

type WorkflowReportVolumesContainerProps = {
    workflowReportVolumesByCountryResponse: WorkflowReportVolumesByCountryResponse;
    axnCountries: Array<AxnCountry>;
    searchFilters: WorkflowReportSearchFiltersState;
    isLoading: boolean;
};

export const WorkflowReportVolumesByCountryContainer = (props: WorkflowReportVolumesContainerProps) => {
    const {
        workflowReportVolumesByCountryResponse,
        axnCountries,
        searchFilters,
        isLoading
    } = props;

    const mergedWorkflowReportVolumesByCountryResponse = useMemo(() => {
        const mergedResponse: WorkflowReportVolumesByCountryResponse = {
            countries: {},
            interval: null
        };

        if (workflowReportVolumesByCountryResponse !== null) {
            mergedResponse.interval = workflowReportVolumesByCountryResponse.interval
            Object.keys(workflowReportVolumesByCountryResponse.countries).forEach(country => {
                const axnCountry = axnCountries.find(ac => ac.alpha2 === country);
                // if we find a valid country - leave it as is
                if (axnCountry) {
                    mergedResponse.countries[country] = workflowReportVolumesByCountryResponse.countries[country];
                } else if (mergedResponse.countries[resources.common.notAvailable]) {
                    // didnt find a valid country - so aggregate the data into "N/A" country
                    mergedResponse.countries[resources.common.notAvailable].forEach(countryData => {
                        // look for the matching date - if we find it, aggregate the value
                        const matchingDateData = workflowReportVolumesByCountryResponse.countries[country].find(dateData => dateData.date === countryData.date);
                        if (matchingDateData) {
                            countryData.value += matchingDateData.value;
                        }
                    });
                } else {
                    // N/A country hasn't been created yet - so create it now with current (invalid) countries data
                    mergedResponse.countries[resources.common.notAvailable] = workflowReportVolumesByCountryResponse.countries[country];
                }
            });
        }


        return mergedResponse;
    }, [axnCountries, workflowReportVolumesByCountryResponse]);

    return (
        <CardContainer>
            <ContentPageHeader title={resources.pages.workflowReport.sections.volumesByCountry.title} />
            <ContentPageBody isMaxWidth={true}>
                {isLoading && <LoadingIndicator title={resources.common.loadingIndicatorText} />}
                {(!isLoading && workflowReportVolumesByCountryResponse && axnCountries) &&
                    <>
                        <span id="workflow-report-volume-by-country" dangerouslySetInnerHTML={{ __html: resources.pages.workflowReport.sections.volumesByCountry.description }} />
                        {Object.keys(mergedWorkflowReportVolumesByCountryResponse.countries).length > 0 && (
                            <>
                                <div className={styles.graphContainer}>
                                    <div className={styles.workflowReportVolumesLineChartContainer}>
                                        <WorkflowReportVolumesByCountryChart
                                            workflowReportVolumesByCountryResponse={mergedWorkflowReportVolumesByCountryResponse}
                                            axnCountries={axnCountries}
                                            searchFilters={searchFilters}
                                        />
                                    </div>
                                </div>
                            </>

                        )}
                    </>
                }

            </ContentPageBody>
        </CardContainer>
    );
};